import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, MapPin, Building2, ListTodo, Mail, FolderOpen, Sun, Cloud, CloudRain, CloudLightning, Wind, Droplets, CalendarDays, Users, HardHat } from "lucide-react";
import { api } from "../lib/api";
import { SiteFormModal, SITE_STATUS } from "../components/cantieri/SiteFormModal";

const WeatherIcon = ({ code, className = "w-4 h-4" }) => code >= 95 ? <CloudLightning className={className}/> : code >= 51 ? <CloudRain className={className}/> : code >= 2 ? <Cloud className={className}/> : <Sun className={className}/>;

const SiteWeather = ({ site }) => {
  const [weather, setWeather] = useState(null);
  useEffect(() => {
    if (site.latitude == null || site.longitude == null) { setWeather(null); return; }
    let mounted = true;
    api.siteWeather(site.id).then((r) => { if (mounted) setWeather(r); }).catch(() => { if (mounted) setWeather(null); });
    return () => { mounted = false; };
  }, [site.id, site.latitude, site.longitude]);
  if (!weather?.available) return null;
  const c = weather.current || {};
  const today = weather.days?.[0] || {};
  return <div className="mx-4 mt-3 rounded-lg bg-sky-50 border border-sky-100 px-3 py-2 flex items-center gap-3 text-xs text-sky-900">
    <WeatherIcon code={c.weather_code} className="w-5 h-5 text-sky-600 shrink-0"/>
    <strong className="text-base">{c.temperature_2m != null ? Math.round(c.temperature_2m) + "°" : "—"}</strong>
    <span className="inline-flex items-center gap-1"><Droplets className="w-3.5 h-3.5"/> {today.precip_probability ?? 0}%</span>
    <span className="inline-flex items-center gap-1"><Wind className="w-3.5 h-3.5"/> {c.wind_speed_10m != null ? Math.round(c.wind_speed_10m) + " km/h" : "—"}</span>
  </div>;
};

const localDay = (d) => [d.getFullYear(),String(d.getMonth()+1).padStart(2,"0"),String(d.getDate()).padStart(2,"0")].join("-");
const mondayOf = (d) => { const x=new Date(d); x.setHours(12,0,0,0); x.setDate(x.getDate()-(x.getDay()+6)%7); return localDay(x); };
const SiteWeekSnapshot = ({ siteId, onClick }) => {
  const [day,setDay] = useState(localDay(new Date()));
  const [work,setWork] = useState([]);
  const [attendance,setAttendance] = useState([]);
  const [workers,setWorkers] = useState([]);
  const [error,setError] = useState(false);
  const week = mondayOf(new Date(day+"T12:00:00"));
  useEffect(() => {
    let active=true;
    setError(false); setWork([]); setAttendance([]);
    Promise.allSettled([
      api.weeklyGantt(siteId,week),
      api.listAttendance({site_id:siteId,date:day}),
      api.listWorkers({active:true}),
    ]).then(([w,a,ws]) => {
      if (!active) return;
      if(w.status==="fulfilled") setWork(w.value.items||[]);
      if(a.status==="fulfilled") setAttendance(a.value||[]);
      if(ws.status==="fulfilled") setWorkers(ws.value||[]);
      setError(w.status==="rejected" || a.status==="rejected");
    });
    return ()=>{active=false;};
  },[siteId,week,day]);
  const end = (w) => {const d=new Date(w.week_start+"T12:00:00");d.setDate(d.getDate()+6);return localDay(d);};
  const dayWork = work.filter(w=>day >= (w.planned_start||w.week_start) && day <= (w.planned_end||end(w)));
  const active = dayWork.filter(w=>w.status==="in_progress");
  const planned = dayWork.filter(w=>w.status==="planned");
  const names = attendance.map(a=>a.worker_name||a.name||workers.find(w=>w.id===a.worker_id)?.name||"Operaio registrato");
  return <div className="mx-4 mt-3 rounded-lg border border-orange-100 bg-orange-50/40 px-3 py-2.5 text-xs" data-testid={`site-week-${siteId}`}>
    <div className="flex items-center justify-between gap-2 mb-2">
      <span className="font-semibold text-slate-800 inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-orange-600"/> Programmazione settimanale</span>
      <input aria-label="Giorno cantiere" type="date" value={day} onChange={e=>e.target.value&&setDay(e.target.value)} className="border border-slate-200 rounded bg-white px-1.5 py-1 text-[11px] max-w-[130px]"/>
    </div>
    <div className="text-[11px] text-slate-500 mb-2">Giorno: {new Date(day+"T12:00:00").toLocaleDateString("it-IT",{weekday:"long",day:"numeric",month:"long"})}</div>
    <div className="flex items-center gap-1.5 text-slate-700 mb-2"><Users className="w-3.5 h-3.5 text-sky-600"/><strong>{attendance.length}</strong> operai registrati{names.length>0&&<span className="truncate text-slate-500" title={names.join(", ")}>· {names.join(", ")}</span>}</div>
    <div className="flex items-center gap-1.5 mb-1 text-slate-700"><HardHat className="w-3.5 h-3.5 text-orange-600"/><strong>{active.length}</strong> in corso · <strong>{planned.length}</strong> programmate</div>
    {dayWork.length>0?<div className="space-y-1">{dayWork.slice(0,4).map(w=><button key={w.id} type="button" onClick={onClick} className="flex items-center gap-2 w-full text-left rounded bg-white border border-slate-100 px-2 py-1 text-slate-700 hover:border-orange-300"><span className={`h-1.5 w-1.5 rounded-full shrink-0 ${w.status==="done"?"bg-emerald-500":w.status==="blocked"?"bg-red-500":w.status==="in_progress"?"bg-orange-500":"bg-sky-500"}`}/><span className="truncate">{w.title}</span></button>)}{dayWork.length>4&&<button onClick={onClick} className="text-orange-700 text-[11px]">+{dayWork.length-4} lavorazioni</button>}</div>:<div className="text-slate-400">Nessuna lavorazione prevista per il giorno</div>}
    {error&&<div role="alert" className="mt-1 text-red-600">Dati parzialmente non disponibili</div>}
  </div>;
};

const SiteCard = ({ s, onClick }) => {
  const st = SITE_STATUS[s.status] || SITE_STATUS.attivo;
  const hasGeo = s.latitude != null && s.longitude != null;
  return (
    <div data-testid={`site-card-${s.id}`} className="bg-white border border-slate-200 hover:border-orange-300 hover:shadow-md rounded-xl overflow-hidden transition-all flex flex-col">
      <button onClick={onClick} className="text-left p-4 pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-display font-semibold text-slate-900 truncate">{s.name}</div>
            {s.address && <div className="text-xs text-slate-500 flex items-start gap-1 mt-1"><MapPin className="w-3 h-3 mt-0.5 shrink-0" /><span>{s.address}</span></div>}
            {s.client_name && <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Building2 className="w-3 h-3 shrink-0" /> {s.client_name}</div>}
          </div>
          <span className={`text-[11px] px-2 py-0.5 rounded border font-semibold shrink-0 ${st.cls}`}>{st.label}</span>
        </div>
      </button>

      {hasGeo ? (
        <div className="px-4">
          <div className="rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
            <iframe
              title={`Mappa ${s.name}`}
              className="w-full h-44 pointer-events-none"
              loading="lazy"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${s.longitude-0.006}%2C${s.latitude-0.004}%2C${s.longitude+0.006}%2C${s.latitude+0.004}&layer=mapnik&marker=${s.latitude}%2C${s.longitude}`}
            />
          </div>
          <div className="text-[10px] text-slate-400 mt-1">© OpenStreetMap contributors</div>
        </div>
      ) : (
        <button onClick={onClick} className="mx-4 rounded-lg border border-dashed border-slate-200 bg-slate-50 h-28 flex items-center justify-center text-xs text-slate-400">
          <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/> Posizione da completare</span>
        </button>
      )}

      <SiteWeather site={s} />

      <SiteWeekSnapshot siteId={s.id} onClick={onClick} />

      {s.next_tasks?.length > 0 && <button onClick={onClick} className="text-left mx-4 mt-3 rounded-lg border border-slate-100 bg-slate-50/70 px-3 py-2">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">Ultime task</div>
        <div className="space-y-1.5">
          {s.next_tasks.slice(0,3).map((t) => <div key={t.id} className="flex items-center gap-2 text-xs">
            <ListTodo className="w-3.5 h-3.5 text-slate-400 shrink-0"/>
            <span className="truncate flex-1 text-slate-700">{t.title}</span>
            {t.due_date && <span className="text-[10px] text-slate-400 shrink-0">{new Date(t.due_date + "T12:00:00").toLocaleDateString("it-IT",{day:"2-digit",month:"short"})}</span>}
          </div>)}
        </div>
      </button>}

      <button onClick={onClick} className="text-left px-4 py-3 mt-2 border-t border-slate-100">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1" data-testid={`site-card-tasks-${s.id}`}><ListTodo className="w-3.5 h-3.5" /> <strong className={s.tasks_open ? "text-orange-600" : ""}>{s.tasks_open}</strong>/{s.tasks_total} task</span>
          <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {s.emails} email</span>
          <span className="inline-flex items-center gap-1"><FolderOpen className="w-3.5 h-3.5" /> {s.files} documenti</span>
          {s.offer_ids?.length > 0 && <span className="ml-auto text-slate-400">{s.offer_ids.length} offert{s.offer_ids.length === 1 ? "a" : "e"}</span>}
        </div>
      </button>
    </div>
  );
};

export default function Cantieri() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sites, setSites] = useState([]);
  const [statusFilter, setStatusFilter] = useState("attivo");
  const [showForm, setShowForm] = useState(location.pathname.endsWith("/nuovo"));
  useEffect(() => { if (location.pathname.endsWith("/nuovo")) setShowForm(true); }, [location.pathname]);

  const load = useCallback(async () => {
    const s = await api.listSites();
    setSites(s);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener("edil:refresh", load); return () => window.removeEventListener("edil:refresh", load); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-900">Dashboard Cantieri</h1>
          <p className="text-slate-500 mt-1 text-sm">Vista sintetica dei cantieri con posizione, stato e dati principali.</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="new-site-button" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg"><Plus className="w-4 h-4" /> Nuovo cantiere</button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="site-status-menu">
        {[["attivo","Cantieri attivi"],["sospeso","Sospesi"],["chiuso","Chiusi"]].map(([key,label]) => {
          const count = sites.filter((x) => x.status === key).length;
          const active = statusFilter === key;
          return <button key={key} onClick={() => setStatusFilter(key)} className={`px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${active ? "bg-orange-600 text-white border-orange-600" : "bg-white text-slate-600 border-slate-200 hover:border-orange-300"}`}>
            {label}<span className={`ml-2 text-xs ${active ? "text-orange-100" : "text-slate-400"}`}>{count}</span>
          </button>;
        })}
      </div>

      <section>
        {sites.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-sm text-slate-400" data-testid="sites-empty">
            Nessun cantiere. Creane uno con "Nuovo cantiere" o usa "Crea cantiere" da un'offerta.
          </div>
        ) : (() => {
          const visible = sites.filter((x) => x.status === statusFilter);
          if (!visible.length) return <div className="bg-white border border-dashed border-slate-200 rounded-xl p-8 text-center text-sm text-slate-400">Nessun cantiere in questa sezione.</div>;
          return <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{visible.map((x) => <SiteCard key={x.id} s={x} onClick={() => navigate(`/cantieri/${x.id}`)} />)}</div>;
        })()}
      </section>

      {showForm && <SiteFormModal onClose={() => { setShowForm(false); if (location.pathname.endsWith("/nuovo")) navigate("/cantieri"); }} onSaved={(s) => { setShowForm(false); navigate(`/cantieri/${s.id}`); }} />}
    </div>
  );
}
